import { getLocaleTag, useI18n } from "./i18n-hooks";
import { useCallback } from "react";

import { addDays, intlFormat as dateFnsIntlFormat, isSameDay } from "date-fns";
import { TimeSheet } from "../__generated__/graphql";

type IntlFormatParams = Parameters<typeof dateFnsIntlFormat>;
type DateFormatOptions = IntlFormatParams[1];

export const SHORT_DATETIME_FORMAT_OPTIONS: DateFormatOptions = {
    weekday: "long",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
};

export const SHORT_DATE_FORMAT_OPTIONS: DateFormatOptions = {
    weekday: "long",
    year: "numeric",
    month: "short",
    day: "numeric",
};

export const SHORT_DATE_IN_LIST_FORMAT_OPTIONS: DateFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
};

export const IN_LIST_FORMAT: DateFormatOptions = {
    weekday: "short",
    month: "short",
    day: "numeric",
};

export const SHORT_DATE: DateFormatOptions = {
    year: "numeric",
    month: "numeric",
    day: "numeric",
};

export const FULL_DATE_FORMAT: DateFormatOptions = {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
};

export const useIntervalFormatter = () => {
    const i18n = useI18n();
    const intlFormat = useDateFormatter();

    return useCallback(
        (fromDate: Date, toDate: Date) => {
            let toOptions = {};
            let fromOption = {};
            if (fromDate.getFullYear() !== toDate.getFullYear()) {
                fromOption = { day: "numeric", month: "short", year: "numeric" };
                toOptions = { day: "numeric", month: "short", year: "numeric" };
            } else {
                if (fromDate.getMonth() !== toDate.getMonth()) {
                    fromOption = { day: "numeric", month: "short" };
                    toOptions = { day: "numeric", month: "short", year: "numeric" };
                } else {
                    fromOption = { day: "numeric" };
                    toOptions = { day: "numeric", month: "short", year: "numeric" };
                }
            }
            const from = intlFormat(fromDate, fromOption);
            const to = intlFormat(toDate, toOptions);
            return i18n.t("interval", { from, to });
        },
        [intlFormat, i18n],
    );
};

export type TimeSheetRange = Pick<TimeSheet, "fromDate" | "toDate">;

export const useTimesheetsRange = () => {
    const formatDateRange = useDateRangeFormatter();
    return useCallback(
        (timesheets: TimeSheetRange[], stringFormat: "short" | "long" = "short") => {
            if (!timesheets.length) {
                return undefined;
            }

            const { startDate, endDate } = timesheets.reduce<{
                startDate: Date;
                endDate: Date;
            }>((result, timeSheet) => {
                const timesheetFromDate = new Date(timeSheet.fromDate);
                const timesheetToDate = new Date(timeSheet.toDate);

                return {
                    startDate:
                        !result.startDate ||
                        timesheetFromDate.getTime() < result.startDate.getTime()
                            ? timesheetFromDate
                            : result.startDate,
                    endDate:
                        !result.endDate || timesheetToDate.getTime() > result.endDate.getTime()
                            ? timesheetToDate
                            : result.endDate,
                };
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any
            }, {} as any);

            return {
                formattedString: formatDateRange(startDate, endDate, stringFormat),
                rawStartDate: startDate,
                rawEndDate: endDate,
            };
        },
        [formatDateRange],
    );
};

const getTruncatedTime = (date: Date) => {
    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();

    return `${hours.toString().padStart(2, "0")}h${minutes !== 0 ? minutes.toString().padStart(2, "0") : ""}`;
};

export const useAbsenceRange = () => {
    const dateFormatter = useDateFormatter();
    const i18n = useI18n();
    return useCallback(
        (startDate: Date, endDate: Date) => {
            const formattedStartDate =
                dateFormatter(startDate, {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    timeZone: "UTC",
                }) || "";

            const formattedStartTime = getTruncatedTime(startDate);

            const formattedEndDate =
                dateFormatter(endDate, {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    timeZone: "UTC",
                }) || "";

            const formattedEndTime = getTruncatedTime(endDate);

            return isSameDay(startDate, endDate)
                ? `${formattedEndDate} (${i18n.t("absence.timeFromTo", { from: formattedStartTime, to: formattedEndTime })})`
                : i18n.t("utils.period", {
                      fromDate: `${formattedStartDate} (${formattedStartTime})`,
                      toDate: `${formattedEndDate} (${formattedEndTime})`,
                  });
        },
        [dateFormatter, i18n],
    );
};

export const useDateRangeFormatter = () => {
    const dateFormatter = useDateFormatter();
    const i18n = useI18n();
    return useCallback(
        (
            startDate: Date,
            endDate: Date,
            stringFormat: "short" | "long" = "short",
            displayTime: boolean = false,
            displayYear: boolean = true,
        ) => {
            const formattedStartDate =
                dateFormatter(
                    startDate,
                    stringFormat === "short"
                        ? {
                              day: "2-digit",
                              month: "short",
                              ...(displayTime ? { hour: "2-digit" } : {}),
                          }
                        : {
                              day: "numeric",
                              ...(displayTime || startDate.getMonth() !== endDate.getMonth()
                                  ? { month: "long" }
                                  : {}),
                              ...(displayYear && startDate.getFullYear() !== endDate.getFullYear()
                                  ? { year: "numeric" }
                                  : {}),
                              ...(displayTime ? { hour: "numeric" } : {}),
                          },
                ) || "";

            const formattedEndDate =
                dateFormatter(
                    endDate,
                    stringFormat === "short"
                        ? {
                              day: "2-digit",
                              month: "short",
                              ...(displayTime ? { hour: "2-digit" } : {}),
                          }
                        : {
                              day: "numeric",
                              month: "long",
                              ...(displayYear ? { year: "numeric" } : {}),
                              ...(displayTime ? { hour: "numeric" } : {}),
                          },
                ) || "";

            return isSameDay(startDate, endDate)
                ? formattedEndDate
                : stringFormat === "short"
                  ? `${formattedStartDate} - ${formattedEndDate}`
                  : i18n.t("utils.period", {
                        fromDate: formattedStartDate,
                        toDate: formattedEndDate,
                    });
        },
        [dateFormatter, i18n],
    );
};

export const useDateFormatter = () => {
    return useCallback((date: Date | number | string, options?: DateFormatOptions) => {
        return dateFnsIntlFormat(date, options || SHORT_DATE, { locale: getLocaleTag() });
    }, []);
};

export const useDateTimeFormatter = () => {
    const i18n = useI18n();
    return useCallback(
        (value: Date | number | string) => {
            const options = { locale: getLocaleTag() };
            const date = dateFnsIntlFormat(
                value,
                {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                },
                options,
            );

            const time = dateFnsIntlFormat(
                value,
                {
                    hour: "numeric",
                    minute: "numeric",
                },
                options,
            );

            return i18n.t("dateTimeAt", { date, time });
        },
        [i18n],
    );
};

export const firstDayOfWeek = (date: Date): Date => {
    return addDays(date, -date.getDay());
};

export const lastDayOfWeek = (date: Date): Date => {
    return addDays(date, 6 - date.getDay());
};
